import { stateWord } from "@lafayette/domain";
import type { StaffItineraryOutcome } from "@lafayette/schemas";
import { useState } from "react";

import { Checkbox } from "../../ui/checkbox";
import { TextAreaField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import type { ItineraryCommand, ItineraryCommandResult } from "../commands";
import { issueWords } from "../labels";
import { addDayOperation, type DayForm, dayErrors, type FieldErrors } from "../operations";
import {
  ACKNOWLEDGEABLE_CODE,
  activationGate,
  type Detail,
  MIN_ACKNOWLEDGE_REASON,
} from "../view-model";
import { ItineraryCommandDialog, useItineraryCommandForm } from "./CommandDialog";
import {
  focusFirstInvalidInDialog,
  initialSave,
  MAX_REASON,
  type PlanContext,
  reasonNeeded,
  SaveSettings,
  type SaveState,
  sendOperations,
} from "./save";
import { InputField, ZoneDatalist } from "./TimeFields";

// The plan-level dialogs: add a day, start a staff draft, activate (with the acknowledgement of a travel gap), archive. Activate and
// archive send the `expectedVersion` of the plan the page read. Acknowledgement is offered for `TRAVEL_TIME_EXCEEDS_GAP` only, with a
// reason of at least 10 characters; every other blocking problem keeps the activation off and says what to fix.

type Done = (outcome: StaffItineraryOutcome) => void;

// --- add a day ----------------------------------------------------------------------------------------------------------------

export function AddDayDialog({
  context,
  onDone,
  onClose,
}: Readonly<{ context: PlanContext; onDone: Done; onClose: () => void }>) {
  const [form, setForm] = useState<DayForm>({ date: "", timeZone: "", title: "" });
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const reasonRequired = reasonNeeded(
    context,
    save,
    false,
    command.failure?.kind === "needs_reason",
  );

  function submit() {
    const found: Record<string, string> = { ...dayErrors(form) };
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() => sendOperations(context, [addDayOperation(form)], save));
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title="Add a day"
      description="A day holds the items of one calendar date. Its zone is the zone its clock times are read in."
      confirmLabel="Add day"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <ZoneDatalist />
      <InputField
        id="day-date"
        label="Date"
        type="date"
        value={form.date}
        onChange={(date) => setForm((current) => ({ ...current, date }))}
        error={errors["day-date"]}
        required
      />
      <InputField
        id="day-zone"
        label="Time zone"
        type="text"
        value={form.timeZone}
        onChange={(timeZone) => setForm((current) => ({ ...current, timeZone }))}
        error={errors["day-zone"]}
        list="itinerary-zone-names"
        placeholder="Europe/London"
        required
      />
      <InputField
        id="day-title"
        label="Day title (optional)"
        type="text"
        value={form.title}
        onChange={(title) => setForm((current) => ({ ...current, title }))}
      />
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}

// --- start a staff draft ------------------------------------------------------------------------------------------------------

export function NewDraftDialog({
  tripId,
  run,
  onDone,
  onClose,
}: Readonly<{
  tripId: string;
  run: (request: ItineraryCommand) => Promise<ItineraryCommandResult>;
  onDone: Done;
  onClose: () => void;
}>) {
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | undefined>();
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);

  function submit() {
    if (title.trim() === "") {
      setError("Enter a title for the plan.");
      return focusFirstInvalidInDialog();
    }
    setError(undefined);
    void command.submit(() =>
      run({ command: "createPlan", body: { tripId, title: title.trim(), sections: [] } }),
    );
  }

  return (
    <ItineraryCommandDialog
      title="Start a staff draft"
      description="An empty draft only staff can see. Add days and items, then activate it when it is ready."
      confirmLabel="Start draft"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <InputField
        id="draft-title"
        label="Plan title"
        type="text"
        value={title}
        onChange={setTitle}
        error={error}
        required
      />
    </ItineraryCommandDialog>
  );
}

// --- activate -----------------------------------------------------------------------------------------------------------------

export function ActivateDialog({
  detail,
  stale,
  run,
  onDone,
  onClose,
}: Readonly<{
  /** The plan as it was when the dialog opened. */
  detail: Detail;
  stale: boolean;
  run: (request: ItineraryCommand) => Promise<ItineraryCommandResult>;
  onDone: Done;
  onClose: () => void;
}>) {
  const gate = activationGate(detail);
  const [reason, setReason] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const replaces = detail.itinerary.supersedesItineraryId !== null;
  const reasonRequired = gate.needsAcknowledgement || command.failure?.kind === "needs_reason";

  function submit() {
    const found: Record<string, string> = {};
    const trimmed = reason.trim();
    if (gate.needsAcknowledgement) {
      if (!acknowledge) found.acknowledge = "Tick the box to acknowledge the travel gap.";
      if (trimmed.length < MIN_ACKNOWLEDGE_REASON) {
        found["itinerary-reason"] =
          `Write at least ${MIN_ACKNOWLEDGE_REASON} characters: say why the plan can go ahead.`;
      }
    } else if (reasonRequired && trimmed === "") {
      found["itinerary-reason"] = "Add a reason.";
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() =>
      run({
        command: "activate",
        itineraryId: detail.itinerary.id,
        body: {
          expectedVersion: detail.itinerary.version,
          ...(trimmed === "" ? {} : { reason: trimmed }),
          ...(gate.needsAcknowledgement ? { acknowledgeIssues: [ACKNOWLEDGEABLE_CODE] } : {}),
        },
      }),
    );
  }

  return (
    <ItineraryCommandDialog
      stale={stale}
      title="Activate this draft?"
      description={
        replaces
          ? "It becomes the current plan and the customer sees it. The plan it replaces stays in the revision history."
          : "It becomes the current plan and the customer sees it."
      }
      confirmLabel="Activate plan"
      confirmDisabled={gate.blocked}
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      {gate.blocked ? (
        <Notice tone="warning" role="alert">
          <p>These problems block activation. Staff cannot acknowledge them. Fix them first:</p>
          <ul className="m-0 grid list-disc gap-1 pl-5">
            {gate.blockingCodes.map((code) => (
              <li key={code}>{issueWords(code).title}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {!gate.blocked && gate.needsAcknowledgement ? (
        <>
          <Notice tone="warning" role="alert">
            <p>
              <strong>{issueWords(ACKNOWLEDGEABLE_CODE).title}.</strong>{" "}
              {issueWords(ACKNOWLEDGEABLE_CODE).detail}
            </p>
            <p>
              You can activate the plan anyway. The acknowledgement is recorded with your reason,
              and the plan stays marked as infeasible.
            </p>
          </Notice>
          <div className="grid min-h-11 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8">
            <Checkbox
              id="acknowledge-gap"
              checked={acknowledge}
              aria-invalid={errors.acknowledge ? true : undefined}
              aria-describedby={errors.acknowledge ? "acknowledge-gap-error" : undefined}
              onChange={(event) => setAcknowledge(event.currentTarget.checked)}
            />
            <label
              htmlFor="acknowledge-gap"
              className="flex min-h-11 items-center text-sm compact:min-h-8"
            >
              I acknowledge that the travel time is longer than the gap.
            </label>
            {errors.acknowledge ? (
              <p
                id="acknowledge-gap-error"
                className="col-start-2 m-0 text-sm font-semibold text-destructive"
              >
                <span aria-hidden="true">⚠ </span>
                {errors.acknowledge}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
      <TextAreaField
        id="itinerary-reason"
        label="Reason (staff only)"
        value={reason}
        onChange={setReason}
        rows={3}
        maxLength={MAX_REASON}
        required={reasonRequired}
        error={errors["itinerary-reason"]}
        hint={
          gate.needsAcknowledgement
            ? `Required, at least ${MIN_ACKNOWLEDGE_REASON} characters. It goes into the audit history. Never shown to the customer.`
            : "Optional. It goes into the audit history. Never shown to the customer."
        }
      />
    </ItineraryCommandDialog>
  );
}

// --- archive ------------------------------------------------------------------------------------------------------------------

export function ArchiveDialog({
  detail,
  stale,
  run,
  onDone,
  onClose,
}: Readonly<{
  detail: Detail;
  stale: boolean;
  run: (request: ItineraryCommand) => Promise<ItineraryCommandResult>;
  onDone: Done;
  onClose: () => void;
}>) {
  const withdrawing = detail.itinerary.status === "ACTIVE";
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const reasonRequired = withdrawing || command.failure?.kind === "needs_reason";

  function submit() {
    const trimmed = reason.trim();
    const found: Record<string, string> = {};
    if (reasonRequired && trimmed === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() =>
      run({
        command: "archive",
        itineraryId: detail.itinerary.id,
        body: {
          expectedVersion: detail.itinerary.version,
          ...(trimmed === "" ? {} : { reason: trimmed }),
        },
      }),
    );
  }

  return (
    <ItineraryCommandDialog
      stale={stale}
      title={withdrawing ? "Withdraw the current plan?" : "Discard this draft?"}
      description={
        withdrawing
          ? `The ${stateWord("itinerary", "staffShort", "ACTIVE").toLowerCase()} is archived and the trip has no current plan until another is activated. It stays in the revision history.`
          : "The draft is archived and nobody can edit it again. It stays in the revision history."
      }
      confirmLabel={withdrawing ? "Withdraw plan" : "Discard draft"}
      tone="danger"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <TextAreaField
        id="itinerary-reason"
        label="Reason (staff only)"
        value={reason}
        onChange={setReason}
        rows={3}
        maxLength={MAX_REASON}
        required={reasonRequired}
        error={errors["itinerary-reason"]}
        hint={
          reasonRequired
            ? "Required. It goes into the audit history. Never shown to the customer."
            : "Optional. It goes into the audit history. Never shown to the customer."
        }
      />
    </ItineraryCommandDialog>
  );
}
